import { API_BASE } from '../../config/env';
import { httpConstants } from '../../constants';
import { httpService } from '../../utility/httpService';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

export interface ChatQuery {
  report?: string;
  impact?: string;
  user_input: string;
}
export interface ChatMessage {
  id: string;
  query: ChatQuery;
  answer?: string;
  timeTaken?: number;
  metadata?: any;
  status?: string;
  files?: {
    urls: string[];
  };
  created_at?: string;
  updated_at?: string;
  reportId?: string;
  report?: Record<string, any>;
  version?: string;
  impact?: string;
}

export interface ChatSession {
  data: ChatMessage[];
  total: number;
}

export interface VersionData {
  [version: string]: any;
}

export interface LatestQueryHistory {
  id: string;
  use_case_id: string;
  conversation_id: string;
  created_by_user_name: string;
  use_case_title: string;
  session_name: string;
  field_type: string;
  version: string;
  semantic_knowledge: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface LatestQueryHistoryResponse {
  data: LatestQueryHistory[];
  total: number;
}

export interface QueryUseCasePayload {
  useCaseId: string;
  query: {
    report?: string;
    user_query: string;
    taxonomy: {
      domain: string;
      function: string;
      process_group: string;
      process: string;
    };
    user_file_data: any[];
  };
  domain: string;
  function: string;
  department: string;
  subDepartment: string;
  files: string[];
  tenantId: string;
  field_type: string;
  file_summary: string;
  organization_id: string;
}

export interface QueryUseCaseResponse {
  botMessage: string;
  discoveryContent: {
    report: string;
    file_summary: string;
  };
  queryId: string;
  sessionName: string;
  impact: string;
  version: string;
  apiTimeTaken: number;
  tokenUsage: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
}

// Add this function at the end of the file
export async function queryUseCase(
  payload: QueryUseCasePayload
): Promise<QueryUseCaseResponse | undefined> {
  const url = `${API_BASE}/api/v1/ideation/query-use-case/`;
  try {
    const response = await httpService<QueryUseCaseResponse>(
      httpConstants.METHOD_TYPE.POST,
      url,
      payload
    );
    console.log('Query Use Case Response:', response);
    return response;
  } catch (error) {
    console.error('Failed to query use case:', error);
    return undefined;
  }
}

export async function getLatestQueryHistory(
  limit: number = 10,
  organization_id: string,
  fieldType?: string,
  showOnlyReports: boolean = true,
  session_name_filter?: string,
  sortBy: string = ''
): Promise<LatestQueryHistoryResponse | undefined> {
  let url = `${API_BASE}/api/v1/ideation/query-history/latest/?limit=${limit}&organization_id=${organization_id}&sortBy=${sortBy}`;

  // Add field_type parameter if provided
  if (session_name_filter) {
    url += `&session_name=${session_name_filter}`;
  }

  if (fieldType && !session_name_filter) {
    url += `&field_type=${fieldType === 'All' ? '' : fieldType}`;
  }
  url += `&showOnlyReports=${showOnlyReports}`;

  try {
    let response = await httpService<LatestQueryHistoryResponse>(
      httpConstants.METHOD_TYPE.GET,
      url
    );
    response.data = response.data.map((item) => ({
      ...item,
      created_at: dayjs.utc(item.created_at).local().format('YYYY-MM-DD HH:mm:ss'),
    }));
    return response;
  } catch (error) {
    console.error('Failed to fetch latest query history:', error);
    return undefined;
  }
}
export interface FileUploadResponse {
  files: string[];
  upload: { id: string };
}

export async function uploadFile(file: File): Promise<FileUploadResponse | undefined> {
  const formData = new FormData();
  formData.append('upload', file, file.name);

  try {
    const response = await httpService<FileUploadResponse>(
      httpConstants.METHOD_TYPE.POST,
      `${API_BASE}/api/data-xplr/nexus/files`,
      formData
    );
    return response;
  } catch (error) {
    console.error('Failed to upload file:', error);
    return undefined;
  }
}
export async function getUseCaseDetails(usecaseId: string): Promise<any> {
  const url = `${API_BASE}/api/v1/ideation/use-cases/${usecaseId}/`;

  try {
    const response = await httpService<any>(httpConstants.METHOD_TYPE.GET, url);
    return response;
  } catch (error) {
    console.error('Failed to fetch diagram data:', error);
    return undefined;
  }
}

export async function getChatHistory(usecaseId: string): Promise<any> {
  const url = `${API_BASE}/api/v1/ideation/query-history/${usecaseId}/?skip=0&limit=1000&sortingKey=created_at&sortingOrder=1`;

  try {
    const response = await httpService<any>(httpConstants.METHOD_TYPE.GET, url);
    return response;
  } catch (error) {
    console.error('Failed to fetch diagram data:', error);
    return undefined;
  }
}

export async function postQuery(payload: any): Promise<any | undefined> {
  const url = `${API_BASE}/api/v1/ideation/query-use-case/`;
  try {
    const response = await httpService<any>(httpConstants.METHOD_TYPE.POST, url, payload);
    console.log('Tech Landscape Diagram Response:', response);
    return response;
  } catch (error) {
    console.error('Failed to fetch tech landscape diagram:', error);
    return undefined;
  }
}

export async function editContent(payload: any): Promise<any> {
  const url = `${API_BASE}/api/v1/ideation/query-use-case/${payload.useCaseId}/`;
  try {
    const response = await httpService<any>(httpConstants.METHOD_TYPE.PUT, url, payload);
    return response;
  } catch (error) {
    console.error('Failed to edit content:', error);
    return error;
  }
}

export async function updateDraftStatus(useCaseId: string, payload: any): Promise<any> {
  const url = `${API_BASE}/api/v1/ideation/use-cases/${useCaseId}/`;
  try {
    const response = await httpService<any>(httpConstants.METHOD_TYPE.PATCH, url, payload);
    return response;
  } catch (error) {
    console.error('Failed to edit content:', error);
    return error;
  }
}

export const apiUpdateOpportunityDetails = async (id: number, payload: any) => {
  try {
    const url = `${API_BASE}/api/v1/e2e_taxonomy/opportunities/${id}/`;
    const response = await httpService(httpConstants.METHOD_TYPE.PATCH, url, payload);
    return response;
  } catch (error) {
    console.error('Failed to edit content:', error);
    return error;
  }
};

export const deleteIdeationIntake = async (useCaseId: string, type: string) => {
  const url = `${API_BASE}/api/v1/ideation/intake/?use_case_id=${useCaseId}&type=${type}`;
  try {
    const response = await httpService(httpConstants.METHOD_TYPE.DELETE, url);
    return response;
  } catch (error) {
    console.error('Failed to delete ideation intake:', error);
    return error;
  }
};
